import { useEffect, useState, useCallback } from 'react';
import {
  Settings,
  MapPin,
  Users as UsersIcon,
  Plus,
  Pencil,
  Trash2,
  X,
  Save,
  Loader2,
  Phone,
  CheckCircle2,
  Power,
  PowerOff,
  AlertCircle,
  RefreshCcw,
  Cloud,
} from 'lucide-react';
import {
  agendamentoAPI,
  type SlotLimit,
  type Tecnico,
} from '../../services/api';
import { useModalKeys } from '../../hooks/useModalKeys';

type Tab = 'cidades' | 'tecnicos';

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'cidades', label: 'Cidades & Vagas', icon: <MapPin size={16} /> },
  { id: 'tecnicos', label: 'Técnicos', icon: <UsersIcon size={16} /> },
];

export default function AgendamentoConfiguracoes() {
  const [tab, setTab] = useState<Tab>('cidades');

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <header className="mb-6 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-primary-soft/40 text-primary flex items-center justify-center">
          <Settings size={24} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-fg">Configurações de Agendamento</h1>
          <p className="text-sm text-fg-2">
            Cidades atendidas, vagas por período e equipes técnicas
          </p>
        </div>
      </header>

      <div className="border-b border-line mb-6 flex gap-1">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
                active
                  ? 'border-primary text-primary'
                  : 'border-transparent text-fg-2 hover:text-fg'
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'cidades' && <CidadesSection />}
      {tab === 'tecnicos' && <TecnicosSection />}
    </div>
  );
}

// ============================================================
// CIDADES & VAGAS
// ============================================================

function CidadesSection() {
  const [items, setItems] = useState<SlotLimit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SlotLimit | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await agendamentoAPI.slotLimits.list();
      setItems(list.sort((a, b) => a.cidade.localeCompare(b.cidade)));
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete(item: SlotLimit): Promise<void> {
    if (!confirm(`Remover a cidade "${item.cidade}"? Agendamentos existentes não são afetados.`))
      return;
    try {
      await agendamentoAPI.slotLimits.remove(item.id);
      await load();
    } catch (err) {
      alert(extractError(err));
    }
  }

  async function handleToggleActive(item: SlotLimit): Promise<void> {
    try {
      await agendamentoAPI.slotLimits.update(item.id, { active: !item.active });
      await load();
    } catch (err) {
      alert(extractError(err));
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-fg-2">
          {items.length} cidade{items.length === 1 ? '' : 's'} cadastrada
          {items.length === 1 ? '' : 's'}
        </p>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary-solid hover:bg-primary-solid-hover text-white rounded-lg text-sm font-medium transition"
        >
          <Plus size={14} />
          Nova cidade
        </button>
      </div>

      {error && <ErrorBanner message={error} />}

      {loading ? (
        <SkeletonList />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<MapPin size={32} />}
          title="Nenhuma cidade cadastrada"
          desc='Adicione cidades pra controlar quantos agendamentos por turno são aceitos.'
        />
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.id}
              className={`bg-surface border rounded-xl p-4 flex items-center gap-3 transition ${
                item.active ? 'border-line' : 'border-canvas opacity-60'
              }`}
            >
              <div className="w-10 h-10 rounded-lg bg-primary-soft/40 text-primary flex items-center justify-center flex-shrink-0">
                <MapPin size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-fg truncate">{item.cidade}</span>
                  {!item.active && (
                    <span className="text-xs text-danger bg-danger-soft/30 px-2 py-0.5 rounded-full">
                      Inativa
                    </span>
                  )}
                </div>
                <p className="text-xs text-fg-2">
                  Manhã: <strong className="text-fg">{item.manha}</strong> · Tarde:{' '}
                  <strong className="text-fg">{item.tarde}</strong>
                </p>
              </div>
              <button
                onClick={() => void handleToggleActive(item)}
                className="p-2 text-muted hover:text-fg-2"
                title={item.active ? 'Desativar' : 'Ativar'}
              >
                {item.active ? <Power size={16} /> : <PowerOff size={16} />}
              </button>
              <button
                onClick={() => {
                  setEditing(item);
                  setModalOpen(true);
                }}
                className="p-2 text-muted hover:text-fg-2"
                title="Editar"
              >
                <Pencil size={16} />
              </button>
              <button
                onClick={() => void handleDelete(item)}
                className="p-2 text-danger hover:text-danger"
                title="Remover"
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {modalOpen && (
        <CidadeModal
          editing={editing}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

function CidadeModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: SlotLimit | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [cidade, setCidade] = useState(editing?.cidade ?? '');
  const [manha, setManha] = useState(String(editing?.manha ?? 0));
  const [tarde, setTarde] = useState(String(editing?.tarde ?? 0));
  const [active, setActive] = useState(editing?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    const manhaN = Number(manha);
    const tardeN = Number(tarde);
    if (!cidade.trim()) {
      setError('Cidade é obrigatória.');
      return;
    }
    if (!Number.isFinite(manhaN) || !Number.isFinite(tardeN) || manhaN < 0 || tardeN < 0) {
      setError('Manhã e tarde devem ser números >= 0.');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await agendamentoAPI.slotLimits.update(editing.id, {
          manha: manhaN,
          tarde: tardeN,
          active,
        });
      } else {
        await agendamentoAPI.slotLimits.create({
          cidade: cidade.trim().toUpperCase(),
          manha: manhaN,
          tarde: tardeN,
          active,
        });
      }
      onSaved();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title={editing ? 'Editar cidade' : 'Nova cidade'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4 p-5">
        <div>
          <label className="block text-sm font-medium text-fg-2 mb-1">Cidade</label>
          <input
            value={cidade}
            onChange={(e) => setCidade(e.target.value)}
            disabled={!!editing}
            placeholder="SOROCABA"
            autoCapitalize="characters"
            className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm uppercase disabled:opacity-60 focus:border-primary focus:outline-none"
          />
          {editing && (
            <p className="text-xs text-muted mt-1">
              Nome não editável. Pra renomear: remova e crie de novo.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <NumberField label="Vagas manhã" value={manha} onChange={setManha} />
          <NumberField label="Vagas tarde" value={tarde} onChange={setTarde} />
        </div>

        <label className="flex items-center gap-2 text-sm text-fg-2">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="rounded border-active bg-canvas"
          />
          Cidade ativa (aparece na busca pública)
        </label>

        {error && <ErrorBanner message={error} />}

        <FormFooter saving={saving} onClose={onClose} editing={!!editing} />
      </form>
    </ModalShell>
  );
}

// ============================================================
// TÉCNICOS
// ============================================================

function TecnicosSection() {
  const [items, setItems] = useState<Tecnico[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Tecnico | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await agendamentoAPI.tecnicos.list();
      setItems(list);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete(item: Tecnico): Promise<void> {
    if (!confirm(`Remover técnico "${item.nome}"?`)) return;
    try {
      await agendamentoAPI.tecnicos.remove(item.id);
      await load();
    } catch (err) {
      alert(extractError(err));
    }
  }

  async function handleToggleActive(item: Tecnico): Promise<void> {
    try {
      await agendamentoAPI.tecnicos.update(item.id, { active: !item.active });
      await load();
    } catch (err) {
      alert(extractError(err));
    }
  }

  async function handleSync(): Promise<void> {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const r = await agendamentoAPI.tecnicos.sync();
      setSyncResult(r.message);
      setTimeout(() => setSyncResult(null), 6000);
      await load();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSyncing(false);
    }
  }

  // Agrupa por equipe
  const grouped = items.reduce<Map<string, Tecnico[]>>((map, t) => {
    const arr = map.get(t.equipe) ?? [];
    arr.push(t);
    map.set(t.equipe, arr);
    return map;
  }, new Map());

  const equipes = [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b));

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <p className="text-sm text-fg-2">
          {items.length} técnico{items.length === 1 ? '' : 's'} ·{' '}
          {grouped.size} equipe{grouped.size === 1 ? '' : 's'} ·{' '}
          {items.filter((t) => t.active).length} ativo(s)
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleSync()}
            disabled={syncing}
            title="Importa os técnicos cadastrados no ERP (novos entram desativados)"
            className="flex items-center gap-2 px-4 py-2 bg-canvas hover:bg-hover border border-line text-fg rounded-lg text-sm font-medium transition disabled:opacity-60"
          >
            <RefreshCcw size={14} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Sincronizando…' : 'Sincronizar ERP'}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-solid hover:bg-primary-solid-hover text-white rounded-lg text-sm font-medium transition"
          >
            <Plus size={14} />
            Novo técnico
          </button>
        </div>
      </div>

      {syncResult && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-success-soft/20 border border-success-line/50 text-sm text-success">
          <CheckCircle2 size={16} />
          {syncResult} Novos técnicos entram <strong>desativados</strong> — ative os que
          devem aparecer na Agenda.
        </div>
      )}

      {error && <ErrorBanner message={error} />}

      {loading ? (
        <SkeletonList />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<UsersIcon size={32} />}
          title="Nenhum técnico cadastrado"
          desc="Adicione técnicos e organize por equipe pra atribuir nos agendamentos."
        />
      ) : (
        <div className="space-y-6">
          {equipes.map(([equipe, list]) => (
            <section key={equipe}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-primary mb-2 px-1">
                Equipe {equipe} ({list.length})
              </h3>
              <ul className="space-y-2">
                {list.map((t) => (
                  <li
                    key={t.id}
                    className={`bg-surface border rounded-xl p-4 flex items-center gap-3 transition ${
                      t.active ? 'border-line' : 'border-canvas opacity-60'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary-solid/30 text-primary flex items-center justify-center font-medium flex-shrink-0">
                      {t.nome.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="font-medium text-fg truncate">{t.nome}</span>
                        {t.erpId && (
                          <span
                            className="flex items-center gap-1 text-xs text-primary bg-primary-soft/30 border border-primary-line/50 px-2 py-0.5 rounded-full"
                            title={`Sincronizado do ERP (id ${t.erpId})`}
                          >
                            <Cloud size={10} />
                            {t.username || 'ERP'}
                          </span>
                        )}
                        {!t.active && (
                          <span className="text-xs text-danger bg-danger-soft/30 px-2 py-0.5 rounded-full">
                            Inativo
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-fg-2 flex-wrap">
                        {t.telefone && (
                          <span className="flex items-center gap-1">
                            <Phone size={11} />
                            {t.telefone}
                          </span>
                        )}
                        {t.cidades.length > 0 ? (
                          <span className="flex items-center gap-1">
                            <MapPin size={11} />
                            {t.cidades.slice(0, 3).join(', ')}
                            {t.cidades.length > 3 && ` +${t.cidades.length - 3}`}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-muted italic">
                            <MapPin size={11} /> Todas as cidades
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => void handleToggleActive(t)}
                      className="p-2 text-muted hover:text-fg-2"
                      title={t.active ? 'Desativar' : 'Ativar'}
                    >
                      {t.active ? <Power size={16} /> : <PowerOff size={16} />}
                    </button>
                    <button
                      onClick={() => {
                        setEditing(t);
                        setModalOpen(true);
                      }}
                      className="p-2 text-muted hover:text-fg-2"
                      title="Editar"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => void handleDelete(t)}
                      className="p-2 text-danger hover:text-danger"
                      title="Remover"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {modalOpen && (
        <TecnicoModal
          editing={editing}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

function TecnicoModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: Tecnico | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [nome, setNome] = useState(editing?.nome ?? '');
  const [equipe, setEquipe] = useState(editing?.equipe ?? '');
  const [telefone, setTelefone] = useState(editing?.telefone ?? '');
  const [cidadesText, setCidadesText] = useState((editing?.cidades ?? []).join(', '));
  const [observacoes, setObservacoes] = useState(editing?.observacoes ?? '');
  const [active, setActive] = useState(editing?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!nome.trim()) {
      setError('Nome é obrigatório.');
      return;
    }

    const cidades = cidadesText
      .split(/[,;\n]+/)
      .map((c) => c.trim())
      .filter(Boolean);

    setSaving(true);
    try {
      const payload = {
        nome: nome.trim(),
        equipe: equipe.trim() || 'A DEFINIR',
        telefone: telefone.trim(),
        cidades,
        active,
        observacoes: observacoes.trim(),
      };
      if (editing) {
        await agendamentoAPI.tecnicos.update(editing.id, payload);
      } else {
        await agendamentoAPI.tecnicos.create(payload);
      }
      onSaved();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title={editing ? 'Editar técnico' : 'Novo técnico'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4 p-5">
        <TextField label="Nome" value={nome} onChange={setNome} placeholder="João Silva" required />
        <TextField label="Equipe" value={equipe} onChange={setEquipe} placeholder="A / B / EQUIPE 1" />
        <TextField label="Telefone" value={telefone} onChange={setTelefone} placeholder="(15) 99999-0000" />

        <div>
          <label className="block text-sm font-medium text-fg-2 mb-1">
            Cidades atendidas
          </label>
          <textarea
            value={cidadesText}
            onChange={(e) => setCidadesText(e.target.value)}
            placeholder="SOROCABA, BOITUVA, CERQUILHO"
            rows={2}
            className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
          />
          <p className="text-xs text-muted mt-1">
            Separe por vírgula. Vazio = atende todas as cidades.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-fg-2 mb-1">Observações</label>
          <textarea
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            rows={2}
            placeholder="Ex: motorista certificado, atende final de semana…"
            className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-fg-2">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="rounded border-active bg-canvas"
          />
          Técnico ativo (disponível pra novos agendamentos)
        </label>

        {error && <ErrorBanner message={error} />}

        <FormFooter saving={saving} onClose={onClose} editing={!!editing} />
      </form>
    </ModalShell>
  );
}

// ============================================================
// Helpers compartilhados
// ============================================================

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  // Esc fecha; clique fora NÃO fecha (Fase 30). Enter já submete os
  // <form> internos nativamente.
  useModalKeys(onClose);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center sm:justify-center sm:p-4">
      <div className="bg-surface w-full sm:max-w-md sm:rounded-2xl border-t sm:border border-line flex flex-col max-h-[100vh] sm:max-h-[92vh]">
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <h2 className="font-bold text-fg text-lg">{title}</h2>
          <button type="button" onClick={onClose} className="text-fg-2 hover:text-fg">
            <X size={22} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

function FormFooter({
  saving,
  onClose,
  editing,
}: {
  saving: boolean;
  onClose: () => void;
  editing: boolean;
}) {
  return (
    <div className="pt-2 flex gap-2 border-t border-line -mx-5 -mb-5 px-5 py-3 mt-4">
      <button
        type="button"
        onClick={onClose}
        disabled={saving}
        className="flex-1 h-11 bg-canvas hover:bg-hover rounded-xl text-fg-2 font-medium"
      >
        Cancelar
      </button>
      <button
        type="submit"
        disabled={saving}
        className="flex-1 h-11 bg-primary-solid hover:bg-primary-solid-hover disabled:bg-active rounded-xl text-white font-bold flex items-center justify-center gap-2"
      >
        {saving ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <>
            <Save size={14} />
            {editing ? 'Salvar' : 'Criar'}
          </>
        )}
      </button>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-fg-2 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
      />
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-fg-2 mb-1">{label}</label>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm focus:border-primary focus:outline-none"
      />
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-danger-soft/20 border border-danger-line/50 text-sm text-danger">
      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-2">
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="bg-surface border border-line rounded-xl p-4 h-16 animate-pulse"
        />
      ))}
    </div>
  );
}

function EmptyState({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-10 text-center">
      <div className="w-14 h-14 rounded-2xl bg-canvas mx-auto flex items-center justify-center mb-4 text-muted">
        {icon}
      </div>
      <p className="font-semibold text-fg mb-1">{title}</p>
      <p className="text-sm text-fg-2 max-w-md mx-auto">{desc}</p>
    </div>
  );
}

function extractError(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e.response?.data?.message ?? e.message ?? 'Erro desconhecido.';
}
